
import { Component, Prop, Vue } from "vue-property-decorator";
import { IFabricName, composeDisplayName } from "@/models/fabric";

/**
 * Name Group / Color Name / Display Name, shared by the add and edit forms.
 *
 * Display Name is the concatenation of the other two ("Canvas" + "Navy" →
 * "Canvas Navy") — which is what staff were typing by hand, or pasting, every
 * time. So it composes itself, and keeps following the two fields it's built
 * from, until someone types in it. From that point it's theirs and we stop
 * touching it; the "Reset to …" link hands it back to us.
 *
 * The link is one-way: this only ever writes Display Name FROM the parts, never
 * parses parts back out of it. A display name isn't reliably decomposable (a
 * legacy fabric can carry a display name that matches neither field), and
 * guessing wrong would silently rewrite the fabric's identity fields.
 *
 * Mutates the passed `name` object directly, following the same convention as
 * BrandFields — the parent owns the fabric doc; this owns three of its fields.
 */
@Component
export default class FabricNameFields extends Vue {
  @Prop({ required: true }) readonly name!: IFabricName;
  /** Dotted keys of the required fields currently missing — see missingFieldKeys(). */
  @Prop({ default: () => ({}) }) readonly invalid!: Record<string, boolean>;

  /**
   * Has the user taken Display Name over?
   *
   * Seeded from the data rather than defaulting to false, because the edit form
   * loads fabrics that were named long before this field existed. A fabric whose
   * display name already equals group + color is still "auto" — renaming its
   * color should carry the display name along. One that differs was authored
   * deliberately (or predates the convention), and must not be silently
   * rewritten the first time someone touches an unrelated field.
   */
  protected manual = false;

  created(): void {
    const display = (this.name.display || "").trim();
    this.manual = !!display && display !== this.composed;
  }

  protected get composed(): string {
    return composeDisplayName(this.name);
  }

  protected onPartInput(part: "group" | "color", e: Event): void {
    this.$set(this.name, part, (e.target as HTMLInputElement).value);
    if (!this.manual) this.$set(this.name, "display", this.composed);
  }

  protected onDisplayInput(e: Event): void {
    const value = (e.target as HTMLInputElement).value;
    this.$set(this.name, "display", value);
    // Clearing the field, or typing exactly what we'd have generated, re-links it
    // — the natural way to ask for the default back is to stop overriding it.
    this.manual = !!value.trim() && value.trim() !== this.composed;
  }

  protected relink(): void {
    this.$set(this.name, "display", this.composed);
    this.manual = false;
  }
}
